import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Transition as _Transition, toHandlers as _toHandlers, mergeProps as _mergeProps, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "px-6 md:px-8 pt-6" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { class: "flex items-center justify-start sm:justify-end gap-2 mb-4 flex-wrap" }
const _hoisted_5 = { class: "truncate" }
const _hoisted_6 = { class: "truncate" }
const _hoisted_7 = { class: "space-y-4" }
const _hoisted_8 = ["value"]
const _hoisted_9 = ["value"]
const _hoisted_10 = { class: "relative flex items-center" }
const _hoisted_11 = { class: "overflow-x-auto p-2 -m-2" }
const _hoisted_12 = { class: "text-gray-900 font-medium truncate" }
const _hoisted_13 = { class: "font-mono text-sm truncate" }
const _hoisted_14 = { class: "flex justify-end" }
const _hoisted_15 = {
  key: 0,
  class: "text-center py-12 bg-white"
}

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { ChevronDownIcon, ChevronRightIcon, MagnifyingGlassIcon } from '@heroicons/vue/20/solid'
import { CalendarDaysIcon, FunnelIcon, UsersIcon } from '@heroicons/vue/24/outline'
import { DatePicker } from 'v-calendar'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import NeueTablist from '@/components/neue/NeueTablist.vue'
import TablistGrid from '@/components/neue/TablistGrid.vue'
import TablistHeader from '@/components/neue/TablistHeader.vue'
import TablistRow from '@/components/neue/TablistRow.vue'
import ReportChart from '@/components/reports/ReportChart.vue'
import { dateObjectToServerDay } from '@/helpers/dates'
import { parseReferralCodeUsageReport } from '@/services/reportParser.service'
import api from '@/store/api'

const REFERRAL_CODES_PATH = '/store/referral-program/codes'
const pageSize = 20

export default {
  __name: 'ReferralOverview',
  setup(__props) {

const referralCols = [
  { width: 'minmax(130px, 2fr)' }, // Customer Name
  { width: 'minmax(120px, 1fr)' }, // Referral Code
  { width: 'minmax(70px, 1fr)' }, // Orders
  { width: 'minmax(70px, 1fr)' }, // Sales
  { width: 'minmax(100px, 1fr)' }, // Last Used
  { width: '28px' } // chevron action
]

const route = useRoute()
const router = useRouter()
const store = useStore()

// Tab state
const activeTab = computed(() => (route.path === REFERRAL_CODES_PATH ? 'codes' : 'overview'))

// Referral Codes tab state
const loading = ref(true)
const redemptions = ref([])
const page = ref(1)
const totalRecords = ref(0)
const totalPages = ref(0)
const searchQuery = ref('')
let searchDebounceTimer = null

const startDate = ref(null)
const endDate = ref(null)
const sortByField = ref(null)
const sortDirection = ref(null)

// Referral program reward
const referrerReward = ref('')

// Overview tab state
const reportLoading = ref(true)
const chartData = ref({ labels: [], datasets: [] })
const reportTableData = ref(null)
const isWithin90Days = dateStr => {
  if (!dateStr) return false
  const date = new Date(dateStr)
  const now = new Date()
  return now - date <= 90 * 24 * 60 * 60 * 1000
}

const formatLastUsed = dateStr => {
  if (!dateStr) return '—'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const sortLabel = computed(() => {
  if (!sortByField.value) return 'Sort by'
  if (sortByField.value === 'number_of_orders') {
    return `Number of orders (${sortDirection.value === 'asc' ? 'ascending' : 'descending'})`
  }
  if (sortByField.value === 'total_sales_cents') {
    return `Total sales (${sortDirection.value === 'asc' ? 'ascending' : 'descending'})`
  }
  return 'Sort by'
})

const dateLabel = computed(() => {
  if (!startDate.value && !endDate.value) return 'Date range'
  const formatDate = date => {
    if (!date) return '...'
    const month = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date)
    const day = String(date.getDate()).padStart(2, '0')
    const year = date.getFullYear()
    return `${month}, ${day} ${year}`
  }
  return `${formatDate(startDate.value)} - ${formatDate(endDate.value)}`
})

const fetchRedemptions = async () => {
  loading.value = true

  let url = `referral_programs/referral_discount_program_redemptions?page=${page.value}&page_size=${pageSize}`

  if (searchQuery.value) {
    url += `&search=${encodeURIComponent(searchQuery.value)}`
  }
  if (startDate.value) {
    url += `&start_date=${dateObjectToServerDay(startDate.value)}`
  }
  if (endDate.value) {
    url += `&end_date=${dateObjectToServerDay(endDate.value)}`
  }
  if (sortByField.value && sortDirection.value) {
    url += `&sort_by_field=${sortByField.value}&sort_direction=${sortDirection.value}`
  }

  try {
    const response = await api.call('GET', url)
    redemptions.value = response.data.data || []
    totalRecords.value = response.data.total_records || 0
    totalPages.value = response.data.total_pages || 0
  } catch (error) {
    console.error('Error fetching referral redemptions:', error)
    redemptions.value = []
    totalRecords.value = 0
    totalPages.value = 0
  } finally {
    loading.value = false
  }
}

const fetchReferralProgram = async () => {
  try {
    const res = await api.call('GET', 'referral_discount_programs')
    const program = res.data?.data?.[0]
    if (program) {
      const cents = program.attributes?.referrer_reward_cents || 0
      const currency = program.attributes?.currency || 'USD'
      referrerReward.value = (cents / 100).toLocaleString('en-US', {
        style: 'currency',
        currency
      })
    }
  } catch (error) {
    console.error('Error fetching referral program:', error)
  }
}

const fetchReportData = async () => {
  reportLoading.value = true
  try {
    const end = new Date()
    const start = new Date(end)
    start.setDate(end.getDate() - 90)
    const toYMD = d => d.toISOString().slice(0, 10)

    const params = new URLSearchParams({
      beginning_date: toYMD(start),
      ending_date: toYMD(end),
      group_by: 'day'
    })

    const res = await api.call('GET', `data_downloads/reports/referral_code_usage_report?${params.toString()}`)
    const parsed = parseReferralCodeUsageReport(res, { useCustomerLabel: true })
    chartData.value = parsed.chartData
    reportTableData.value = parsed.tableData
  } catch (error) {
    console.error('Error fetching referral report data:', error)
  } finally {
    reportLoading.value = false
  }
}

const updateSort = (field, direction) => {
  sortByField.value = field
  sortDirection.value = direction
  page.value = 1
  fetchRedemptions()
}

const clearDateFilter = () => {
  startDate.value = null
  endDate.value = null
  page.value = 1
  fetchRedemptions()
}

const prevPage = ret => {
  if (page.value > 1) {
    page.value--
    fetchRedemptions()
    if (ret) ret()
  }
}

const nextPage = ret => {
  if (page.value < totalPages.value) {
    page.value++
    fetchRedemptions()
    if (ret) ret()
  }
}

watch(searchQuery, () => {
  clearTimeout(searchDebounceTimer)
  searchDebounceTimer = setTimeout(() => {
    sortByField.value = null
    sortDirection.value = null
    startDate.value = null
    endDate.value = null
    page.value = 1
    fetchRedemptions()
  }, 300)
})

watch([startDate, endDate], () => {
  page.value = 1
  fetchRedemptions()
})

onMounted(() => {
  if (route.query.tab === 'codes') router.replace(REFERRAL_CODES_PATH)
  fetchReportData()
  fetchReferralProgram()
  fetchRedemptions()
})

return (_ctx, _cache) => {
  const _component_SecondaryTop = _resolveComponent("SecondaryTop")
  const _component_NeueLoading = _resolveComponent("NeueLoading")
  const _component_NeuePagination = _resolveComponent("NeuePagination")
  const _component_SecondaryNav = _resolveComponent("SecondaryNav")

  return (_openBlock(), _createBlock(_component_SecondaryNav, null, {
    top: _withCtx(() => [
      _createVNode(_component_SecondaryTop, {
        text: "Referral program",
        "save-status": false,
        sections: [
          {
            id: 'overview',
            name: 'Overview',
            active: activeTab.value === 'overview',
            action: () => _unref(router).push('/store/referral-program')
          },
          {
            id: 'codes',
            name: 'Referral codes',
            active: activeTab.value === 'codes',
            action: () => _unref(router).push(REFERRAL_CODES_PATH)
          },
          {
            id: 'settings',
            name: 'Settings',
            action: () => _unref(router).push('/store/referral-program/settings')
          }
        ]
      }, null, 8, ["sections"])
    ]),
    main: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (activeTab.value === 'overview')
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createVNode(_component_NeueLoading, { loading: reportLoading.value }, {
                default: _withCtx(() => [
                  _createVNode(ReportChart, {
                    "report-id": "referral-code-usage-overview",
                    title: "Referral code usage",
                    description: "Last 90 days",
                    "empty-state-title": "No usage in the last 90 days",
                    "empty-state-description": "Share your referral codes to start seeing usage data here.",
                    "chart-data": chartData.value,
                    "has-supporting-table": true,
                    "table-data": reportTableData.value,
                    "label-column-title": "Customer Name",
                    "chart-type": "stackedBar",
                    "date-filter": false,
                    "dropdown-filter": false,
                    loading: reportLoading.value,
                    "table-hover-enabled": false,
                    "view-report-link": "/referral-code-usage"
                  }, null, 8, ["chart-data", "table-data", "loading"])
                ]),
                _: 1
              }, 8, ["loading"])
            ]))
          : _createCommentVNode("", true),
        (activeTab.value === 'codes')
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createVNode(_component_NeueLoading, { loading: loading.value }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_4, [
                    _createVNode(_unref(Popover), {
                      as: "div",
                      class: "relative"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(PopoverButton), { class: "inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-500" }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(FunnelIcon), { class: "h-4 w-4 text-gray-500 shrink-0" }),
                            _createElementVNode("span", _hoisted_5, _toDisplayString(sortLabel.value), 1),
                            _createVNode(_unref(ChevronDownIcon), {
                              class: "h-4 w-4 text-gray-500 shrink-0",
                              "aria-hidden": "true"
                            })
                          ]),
                          _: 1
                        }),
                        _createVNode(_Transition, {
                          "enter-active-class": "transition ease-out duration-100",
                          "enter-from-class": "transform opacity-0 scale-95",
                          "enter-to-class": "transform opacity-100 scale-100",
                          "leave-active-class": "transition ease-in duration-75",
                          "leave-from-class": "transform opacity-100 scale-100",
                          "leave-to-class": "transform opacity-0 scale-95"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(PopoverPanel), { class: "absolute right-0 z-20 mt-2 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 px-4 py-1" }, {
                              default: _withCtx(() => [
                                _createVNode(_unref(PopoverButton), {
                                  class: "text-sm font-medium whitespace-nowrap text-gray-900 cursor-pointer my-3 block",
                                  onClick: _cache[0] || (_cache[0] = $event => (updateSort(null, null)))
                                }, {
                                  default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                                    _createTextVNode(" None ", -1)
                                  ]))]),
                                  _: 1
                                }),
                                _createVNode(_unref(PopoverButton), {
                                  class: "text-sm font-medium whitespace-nowrap text-gray-900 cursor-pointer my-3 block",
                                  onClick: _cache[1] || (_cache[1] = $event => (updateSort('number_of_orders', 'asc')))
                                }, {
                                  default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                                    _createTextVNode(" Number of orders (ascending) ", -1)
                                  ]))]),
                                  _: 1
                                }),
                                _createVNode(_unref(PopoverButton), {
                                  class: "text-sm font-medium whitespace-nowrap text-gray-900 cursor-pointer my-3 block",
                                  onClick: _cache[2] || (_cache[2] = $event => (updateSort('number_of_orders', 'desc')))
                                }, {
                                  default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                                    _createTextVNode(" Number of orders (descending) ", -1)
                                  ]))]),
                                  _: 1
                                }),
                                _createVNode(_unref(PopoverButton), {
                                  class: "text-sm font-medium whitespace-nowrap text-gray-900 cursor-pointer my-3 block",
                                  onClick: _cache[3] || (_cache[3] = $event => (updateSort('total_sales_cents', 'asc')))
                                }, {
                                  default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                    _createTextVNode(" Total sales (ascending) ", -1)
                                  ]))]),
                                  _: 1
                                }),
                                _createVNode(_unref(PopoverButton), {
                                  class: "text-sm font-medium whitespace-nowrap text-gray-900 cursor-pointer my-3 block",
                                  onClick: _cache[4] || (_cache[4] = $event => (updateSort('total_sales_cents', 'desc')))
                                }, {
                                  default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                                    _createTextVNode(" Total sales (descending) ", -1)
                                  ]))]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(Popover), {
                      as: "div",
                      class: "relative"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(PopoverButton), { class: "inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-500" }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(CalendarDaysIcon), { class: "h-4 w-4 text-gray-500 shrink-0" }),
                            _createElementVNode("span", _hoisted_6, _toDisplayString(dateLabel.value), 1),
                            _createVNode(_unref(ChevronDownIcon), {
                              class: "h-4 w-4 text-gray-500 shrink-0",
                              "aria-hidden": "true"
                            })
                          ]),
                          _: 1
                        }),
                        _createVNode(_Transition, {
                          "enter-active-class": "transition ease-out duration-100",
                          "enter-from-class": "transform opacity-0 scale-95",
                          "enter-to-class": "transform opacity-100 scale-100",
                          "leave-active-class": "transition ease-in duration-75",
                          "leave-from-class": "transform opacity-100 scale-100",
                          "leave-to-class": "transform opacity-0 scale-95"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(PopoverPanel), { class: "absolute right-0 z-20 mt-2 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 p-4 w-72" }, {
                              default: _withCtx(() => [
                                _createElementVNode("div", _hoisted_7, [
                                  _createElementVNode("div", null, [
                                    _cache[13] || (_cache[13] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-700 mb-1" }, "Start date", -1)),
                                    _createVNode(_unref(DatePicker), {
                                      modelValue: startDate.value,
                                      "onUpdate:modelValue": _cache[5] || (_cache[5] = $event => ((startDate).value = $event)),
                                      mode: "date",
                                      popover: { visibility: 'click' },
                                      masks: { input: 'MMM, DD YYYY' }
                                    }, {
                                      default: _withCtx(({ inputValue, inputEvents }) => [
                                        _createElementVNode("input", _mergeProps({
                                          value: inputValue,
                                          class: "block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm",
                                          placeholder: "Select start date"
                                        }, _toHandlers(inputEvents, true)), null, 16, _hoisted_8)
                                      ]),
                                      _: 1
                                    }, 8, ["modelValue"])
                                  ]),
                                  _createElementVNode("div", null, [
                                    _cache[14] || (_cache[14] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-700 mb-1" }, "End date", -1)),
                                    _createVNode(_unref(DatePicker), {
                                      modelValue: endDate.value,
                                      "onUpdate:modelValue": _cache[6] || (_cache[6] = $event => ((endDate).value = $event)),
                                      mode: "date",
                                      popover: { visibility: 'click' },
                                      masks: { input: 'MMM, DD YYYY' }
                                    }, {
                                      default: _withCtx(({ inputValue, inputEvents }) => [
                                        _createElementVNode("input", _mergeProps({
                                          value: inputValue,
                                          class: "block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm",
                                          placeholder: "Select end date"
                                        }, _toHandlers(inputEvents, true)), null, 16, _hoisted_9)
                                      ]),
                                      _: 1
                                    }, 8, ["modelValue"])
                                  ]),
                                  _createVNode(_unref(PopoverButton), {
                                    class: "text-sm font-medium text-gray-600 hover:text-gray-900 cursor-pointer",
                                    onClick: clearDateFilter
                                  }, {
                                    default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                                      _createTextVNode(" Clear dates ", -1)
                                    ]))]),
                                    _: 1
                                  })
                                ])
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createElementVNode("div", _hoisted_10, [
                      _createVNode(_unref(MagnifyingGlassIcon), { class: "pointer-events-none absolute left-3 h-4 w-4 text-gray-500" }),
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": _cache[7] || (_cache[7] = $event => ((searchQuery).value = $event)),
                        type: "text",
                        class: "h-[34px] w-56 pl-9 pr-3 rounded-full border border-gray-300 bg-white text-sm text-gray-700 placeholder-gray-500 shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500",
                        placeholder: "Search referral codes..."
                      }, null, 512), [
                        [_vModelText, searchQuery.value]
                      ])
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_11, [
                    _createVNode(NeueTablist, null, {
                      head: _withCtx(() => [
                        _createVNode(TablistHeader, null, {
                          default: _withCtx(() => [
                            _createVNode(TablistGrid, {
                              columns: referralCols,
                              header: ""
                            }, {
                              default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                                _createElementVNode("div", null, "Customer Name", -1),
                                _createElementVNode("div", null, "Referral Code", -1),
                                _createElementVNode("div", null, "Orders", -1),
                                _createElementVNode("div", null, "Sales", -1),
                                _createElementVNode("div", null, "Last Used", -1),
                                _createElementVNode("div", null, null, -1)
                              ]))]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(redemptions.value, (item) => {
                          return (_openBlock(), _createBlock(TablistRow, {
                            key: item.id,
                            class: _normalizeClass(isWithin90Days(item.attributes.last_used_at) ? 'cursor-pointer' : ''),
                            onClick: $event => (
                    isWithin90Days(item.attributes.last_used_at) &&
                      _unref(router).push({
                        path: `/store/referral-program/codes/${item.id}`,
                        query: {
                          code: item.attributes.referral_code,
                          discount_code_id: item.attributes.discount_code_id
                        }
                      })
                  )
                          }, {
                            default: _withCtx(() => [
                              _createVNode(TablistGrid, { columns: referralCols }, {
                                default: _withCtx(() => [
                                  _createElementVNode("div", _hoisted_12, _toDisplayString(item.attributes.customer_name), 1),
                                  _createElementVNode("div", _hoisted_13, _toDisplayString(item.attributes.referral_code), 1),
                                  _createElementVNode("div", null, _toDisplayString(item.attributes.number_of_orders), 1),
                                  _createElementVNode("div", null, _toDisplayString(item.attributes.total_sales), 1),
                                  _createElementVNode("div", null, _toDisplayString(formatLastUsed(item.attributes.last_used_at)), 1),
                                  _createElementVNode("div", _hoisted_14, [
                                    _createVNode(_unref(ChevronRightIcon), {
                                      class: _normalizeClass(["h-5 w-5", isWithin90Days(item.attributes.last_used_at) ? 'text-gray-400' : 'invisible'])
                                    }, null, 8, ["class"])
                                  ])
                                ]),
                                _: 2
                              }, 1024)
                            ]),
                            _: 2
                          }, 1032, ["class", "onClick"]))
                        }), 128)),
                        (!loading.value && !redemptions.value.length)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                              _createVNode(_unref(UsersIcon), { class: "mx-auto h-12 w-12 text-gray-400" }),
                              _cache[17] || (_cache[17] = _createElementVNode("h3", { class: "mt-2 text-sm font-medium text-gray-900" }, "No referral redemptions", -1)),
                              _cache[18] || (_cache[18] = _createElementVNode("p", { class: "mt-1 text-sm text-gray-500" }, "No referral codes have been used yet.", -1))
                            ]))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1
                    })
                  ]),
                  (totalRecords.value > pageSize)
                    ? (_openBlock(), _createBlock(_component_NeuePagination, {
                        key: 0,
                        size: totalRecords.value,
                        from: (page.value - 1) * pageSize + 1,
                        to: Math.min(page.value * pageSize, totalRecords.value),
                        onPrev: prevPage,
                        onNext: nextPage
                      }, null, 8, ["size", "from", "to"]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }, 8, ["loading"])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }))
}
}

}